import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { IMainState, IProduct } from '../../interfaces';
import { PageView } from '../../utils/pageView';
import { ChatContentSharingContext } from '../ChatContentSharingContext';
import Header from '../Header';
import { logEvent } from './../../analytics';
import {
  actionSelectProductBuckle,
  actionSelectProductStrap
} from './../../redux/actions';
import {
  DID_SELECT_PRODUCT_BUCKLE,
  DID_SELECT_PRODUCT_STRAP
} from './../../utils/constants';
import { getProductDisplayName } from './../../utils/product';
import BuckleSelector from './BuckleSelector';
import StrapSelector from './StrapSelector';

export const Personalize = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const {
    buckles,
    strapBucklePairs,
    straps,
    selectedBuckle,
    selectedStrap,
    selectedDial,
    activeSection
  } = useSelector((state: IMainState) => state.clientState?.productAccessories);
  const { isChatContentSelection, getSendToShareButton } = React.useContext(
    ChatContentSharingContext
  );

  const currentAvailableBuckles = buckles.filter(
    (buckle) => strapBucklePairs[selectedStrap.id]?.indexOf(buckle.id) !== -1
  );

  const onSelectStrap = (strap: IProduct) => {
    dispatch(actionSelectProductStrap(strap));
    logEvent(DID_SELECT_PRODUCT_STRAP, DID_SELECT_PRODUCT_STRAP, {
      product: selectedDial?.id,
      selectedStrap: strap?.id,
      selectedBuckle: selectedBuckle?.id,
      availableStraps: straps.map((strap) => strap.id)
    });
  };

  const onSelectBuckle = (buckle: IProduct) => {
    dispatch(actionSelectProductBuckle(buckle));
    logEvent(DID_SELECT_PRODUCT_BUCKLE, DID_SELECT_PRODUCT_BUCKLE, {
      product: selectedDial?.id,
      selectedStrap: selectedStrap?.id,
      selectedBuckle: buckle?.id,
      availableBuckles: currentAvailableBuckles.map((buckle) => buckle.id)
    });
  };

  React.useEffect(() => {
    window.location.hash = activeSection;
  }, [activeSection]);

  return (
    <>
      <Header
        title={t('personalize').toUpperCase()}
        shareTitle={`${t('personalized')} ${getProductDisplayName(
          selectedDial,
          language
        )}`}
        shareImage={
          selectedDial.imageUrls.length > 0 && selectedDial.imageUrls[0]
        }
        type={PageView.PRODUCT_PERSONALIZE}
        id={selectedDial.id}
      />
      <div className="personalize-container text-center">
        <div className="row align-items-center">
          <div className="col strap-dial-container" id="strapSection">
            <StrapSelector
              straps={straps}
              onSelectStrap={onSelectStrap}
              currentDial={selectedDial}
              selectedStrap={selectedStrap}
            />
          </div>
          <div className="col-md-auto buckle-container" id="buckleSection">
            <BuckleSelector
              buckles={currentAvailableBuckles}
              selectedBuckle={selectedBuckle}
              currentStrap={selectedStrap}
              onSelectBuckle={onSelectBuckle}
            />
          </div>
        </div>
      </div>
      {isChatContentSelection &&
        getSendToShareButton(
          PageView.PRODUCT_PERSONALIZE,
          `${selectedDial?.id}::${selectedDial?.brandId}::${selectedStrap?.id}::${selectedBuckle?.id}`
        )}
      <style jsx>{`
        .row {
          margin: 0;
          height: calc(100vh - 45px);
        }
        .col {
          padding: 0;
        }
        .buckle-container {
          height: calc(100vh - 45px);
          background: #efefef;
          display: flex;
          justify-content: center;
          border-right: 1px solid #fff;
          width: 350px;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
        }
        .personalize-container {
          overflow: hidden;
          width: 100%;
        }

        .strap-dial-container,
        .buckle-container {
          height: calc(100vh - 45px);
          display: flex;
        }
        .strap-dial-container {
          overflow: hidden;
        }

        .personalize-container * {
          outline: none;
        }

        .strap-dial-container {
          width: 0;
        }
        @media (max-width: 992px) {
          .buckle-container {
            width: 250px;
          }
        }

        @media (max-height: 880px) and (min-width: 768px) {
          .strap-dial-container {
            display: block;
            margin-top: -45px;
            height: 100vh;
          }
        }

        @media (max-width: 768px) {
          .row {
            display: block;
            height: auto;
          }

          .strap-dial-container,
          .buckle-container {
            width: 100%;
          }

          .strap-dial-container {
            margin-top: -45px;
            height: 100vh;
          }
          .buckle-container {
            height: auto;
          }
        }
      `}</style>
    </>
  );
};
